import { createElement, type ComponentType, type ReactNode } from 'react';
import { AdminLayout } from '@/layouts/AdminLayout';
import { HackerPortalLayout } from '@/layouts/HackerPortalLayout';
import { MentorPortalLayout } from '@/layouts/MentorPortalLayout';
import { PublicEditionLayout } from '@/layouts/PublicEditionLayout';

export type PageComponent = ComponentType & {
  layout?: (page: ReactNode) => ReactNode;
};

export type ResolvedComponent = {
  default: PageComponent;
};

// Assigns each page its route-group shell, mirroring the Next route groups this app was
// ported from (G10). Shared by the CSR (eager) and SSR (lazy) resolvers below so they
// can't drift on which pages get which shell.
function attachLayout(name: string, page: ResolvedComponent): ResolvedComponent {
  // Every backoffice page renders inside the admin shell, exactly as the Next
  // app/admin/(protected)/layout.tsx wrapped that whole route group. A page that
  // declares its own layout keeps it.
  if (name.startsWith('Admin/') && !page.default.layout) {
    page.default.layout = (child) => createElement(AdminLayout, null, child);
  }

  // Same for the hacker portal: app/(hack)/hacker/(protected)/layout.tsx wrapped that
  // whole route group, and the shell decides server-side (HackerPortal) whether the
  // sidebar and the ph26 palette are on.
  if (name.startsWith('Hacker/') && !page.default.layout) {
    page.default.layout = (child) => createElement(HackerPortalLayout, null, child);
  }

  // And the mentor portal: app/(hack)/26-{co,mx}/mentor/(protected)/layout.tsx wrapped
  // both edition trees, and MentorPortal decides server-side which edition it is.
  if (name.startsWith('Mentor/') && !page.default.layout) {
    page.default.layout = (child) => createElement(MentorPortalLayout, null, child);
  }

  // The tour-stop route groups (app/(public)/26-{ar,co,mx}/layout.tsx) span five page
  // directories, so there is no prefix to key on: every remaining page gets the shell
  // and it renders nothing at all unless the controller shared `editionShell` (U9).
  if (!page.default.layout) {
    page.default.layout = (child) => createElement(PublicEditionLayout, null, child);
  }

  return page;
}

// CSR (entrypoints/inertia.ts): pages are eagerly bundled into the main chunk so
// resolution is synchronous — no network round-trip on client-side navigation.
export function resolvePage(name: string, pages: Record<string, ResolvedComponent>) {
  const page = pages[`../pages/${name}.tsx`];

  if (!page) {
    console.error(`Missing Inertia page component: '${name}.tsx'`);
    return page;
  }

  return attachLayout(name, page);
}

// SSR (ssr/ssr.tsx): pages load lazily instead, one per request. Eager-loading every
// page here would also evaluate browser-only code at server boot — the arcade showcase's
// three.js CSS3DRenderer references `ProgressEvent`, a DOM global Node doesn't have, and
// crashes the whole SSR process before it can serve a single request.
export async function resolvePageForSsr(
  name: string,
  pages: Record<string, () => Promise<ResolvedComponent>>,
) {
  const loader = pages[`../pages/${name}.tsx`];

  if (!loader) {
    // SSRRenderer (inertia_rails) rescues this and falls back to client-only rendering,
    // same as any other SSR failure.
    throw new Error(`Missing Inertia page component: '${name}.tsx'`);
  }

  // Inertia's SSR resolver type (unlike the sync/CSR one) only accepts
  // `Promise<ReactComponent>`, not `Promise<{ default: ReactComponent }>` — unwrap here.
  return attachLayout(name, await loader()).default;
}
